import { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrimaSquadraPage = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const response = await axios.get(`${API}/players?team=prima_squadra`);
        setPlayers(response.data);
      } catch (error) {
        console.error("Error fetching players:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPlayers();
  }, []);

  // Staff first, then players by position
  const staffPositions = ["allenatore", "vice_allenatore", "preparatore_portieri", "preparatore_atletico", "massaggiatore", "collaboratore_tecnico", "team_manager", "magazziniere"];
  
  const groupedPlayers = {
    staff: players.filter((p) => staffPositions.includes(p.position)),
    portiere: players.filter((p) => p.position === "portiere"),
    difensore: players.filter((p) => p.position === "difensore"),
    centrocampista: players.filter((p) => p.position === "centrocampista"),
    attaccante: players.filter((p) => p.position === "attaccante"),
  };

  const positionLabels = {
    staff: "Staff Tecnico",
    portiere: "Portieri",
    difensore: "Difensori",
    centrocampista: "Centrocampisti",
    attaccante: "Attaccanti",
  };

  const getStaffRoleLabel = (position) => {
    const labels = {
      'allenatore': 'Allenatore',
      'vice_allenatore': 'Vice Allenatore',
      'preparatore_portieri': 'Prep. Portieri',
      'preparatore_atletico': 'Prep. Atletico',
      'massaggiatore': 'Massaggiatore',
      'collaboratore_tecnico': 'Coll. Tecnico',
      'team_manager': 'Team Manager',
      'magazziniere': 'Magazziniere'
    };
    return labels[position] || position;
  };

  // Order for rendering: staff first, then goalkeepers, defenders, midfielders, attackers
  const categoryOrder = ['staff', 'portiere', 'difensore', 'centrocampista', 'attaccante'];

  return (
    <div data-testid="prima-squadra-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 lg:pt-32 pb-12 bg-[#0047AB] noise-overlay">
        <div className="max-w-7xl mx-auto px-4 text-center text-white">
          <h1 className="text-4xl lg:text-5xl font-black uppercase mb-4 animate-fade-in-up">
            Prima Squadra
          </h1>
          <p className="text-blue-200 max-w-xl mx-auto">
            I nostri campioni che portano in alto i colori del Volpiano
          </p>
        </div>
      </section>

      {/* Team Photo */}
      <section className="relative -mt-8 mb-12">
        <div className="max-w-5xl mx-auto px-4">
          <div className="rounded-2xl overflow-hidden shadow-2xl bg-slate-100">
            <img
              src="https://customer-assets.emergentagent.com/job_fef51c73-3fe9-4a0a-b70a-2a7119fa5032/artifacts/plrwn113_55111776-0b2d-406e-9c71-ba90ddb91c34.jpeg"
              alt="Prima Squadra Volpiano"
              className="w-full h-auto object-contain"
              data-testid="team-photo"
            />
          </div>
        </div>
      </section>

      {/* Rosa */}
      <section className="section-padding bg-slate-50" data-testid="players-section">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-8 text-center">
            Rosa e Staff
          </h2>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="animate-pulse bg-white rounded-xl overflow-hidden"
                >
                  <div className="h-48 bg-slate-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            categoryOrder.map((category) => {
              const categoryPlayers = groupedPlayers[category];
              if (!categoryPlayers || categoryPlayers.length === 0) return null;
              
              return (
                <div key={category} className="mb-12">
                  <h3 className="text-xl font-bold text-[#0047AB] mb-6 flex items-center gap-2">
                    <span className="w-8 h-1 bg-[#0047AB] rounded-full" />
                    {positionLabels[category]}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {categoryPlayers.map((player) => (
                      <div
                        key={player.id}
                        data-testid={`player-card-${player.id}`}
                        className="player-card group"
                      >
                        <div className="aspect-[3/4] bg-slate-100 flex items-center justify-center relative overflow-hidden">
                          {player.image_url ? (
                            <img
                              src={player.image_url}
                              alt={player.name || player.surname}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="text-center">
                              <span className="font-accent text-6xl font-extrabold text-[#0047AB]/20">
                                {player.number || "?"}
                              </span>
                            </div>
                          )}
                          {player.number && (
                            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0047AB] to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                              <span className="text-white font-bold">
                                #{player.number}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-4 bg-white">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-bold text-slate-900">
                                {player.name} {player.surname}
                              </h4>
                              <p className="text-sm text-slate-500">
                                {category === 'staff' 
                                  ? getStaffRoleLabel(player.position)
                                  : player.position || ""
                                }
                              </p>
                            </div>
                            {player.number && (
                              <span className="font-accent text-2xl font-bold text-[#0047AB]">
                                {player.number}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrimaSquadraPage;
